import { Col } from "antd";

import { ApplicationConfigurationsValidationsView } from "src/app/features/ApplicationConfigurations/components/validations/ApplicationConfigurationsValidationsView";
import { getValidationRules } from "../Form/components/Validations/ValidationFieldsFactory";
import type { FormComponent, FormFieldFactoryProps } from "../Form/types/types";
import { DisplayText } from "./components/ListComponents/DisplayText";
import { createEditableListFormField } from "./CreateEditableListFormField";
import { CenteredContentCol, RightContentCol } from "./styles/Styles";
import { MAX_COLS } from "./types/Constants";
import type { EditableItem } from "./types/EditableItem";
import { ColumnAlignment, type ColumnWidth, type EditableListColumnConfig, EditableListItemRenderAs } from "./types/EditableListConfig";

export const calculateFreeSpace = (columns: EditableListColumnConfig[]): number => {
    if (!columns) {
        return 0;
    }

    const usedCols = columns
        .map((col) => col.width)
        .reduce((acc, val) => {
            const width = acc + val;
            return width as ColumnWidth;
        }, 0);
    return Math.max(MAX_COLS - usedCols, 1);
};

export const createLayout = (column: EditableListColumnConfig, value: string | React.JSX.Element): React.JSX.Element => {
    const key = `${column.field?.name ?? column.field}-${column.title}`;
    let layout = (
        <Col span={column.width} key={key}>
            {value}
        </Col>
    );
    if (column.alignment === ColumnAlignment.RIGHT) {
        layout = (
            <RightContentCol span={column.width} key={key}>
                {value}
            </RightContentCol>
        );
    } else if (column.alignment === ColumnAlignment.CENTER) {
        layout = (
            <CenteredContentCol span={column.width} key={key}>
                {value}
            </CenteredContentCol>
        );
    }
    return layout;
};

export const createViewColumn = <T extends EditableItem>(column: EditableListColumnConfig, value: T) => {
    if (!value || !column) {
        return null;
    }

    // @ts-ignore
    let val = column.field ? value[column.field.name] : "";
    if (val === undefined) {
        val = "";
    }

    // TODO Create value transformations factory
    let displayValue;
    if (column.type === EditableListItemRenderAs.TEXT) {
        displayValue = String(val);
    } else if (column.type === EditableListItemRenderAs.BOOLEAN) {
        displayValue = val ? "True" : "False";
    } else if (column.type === EditableListItemRenderAs.DATE) {
        displayValue = Date.parse(`${val}`).toLocaleString();
    } else if (column.type === EditableListItemRenderAs.VALIDATIONS_VIEW) {
        displayValue = <ApplicationConfigurationsValidationsView value={val} />;
    } else if (column.type === EditableListItemRenderAs.DEFAULT_VALUE_VIEW) {
        displayValue = <DisplayText value={val.value?.toString()} />;
    } else {
        displayValue = "";
    }

    return createLayout(column, displayValue);
};

export const createEditColumn = (column: EditableListColumnConfig) => {
    if (!column.field) {
        return null;
    }
    const fieldConfig: FormFieldFactoryProps = {
        field: {
            name: column.field.name ?? "",
            component: column.field.component as FormComponent,
            translationKeys: { label: "" },
        },
        rules: getValidationRules(column.field.validationRules),
        readOnly: column.field.readonly || false,
    };

    const field = createEditableListFormField(fieldConfig);

    return createLayout(column, field);
};
